// CISA SCuBA (Secure Cloud Business Applications) Compliance Types

export interface ScubaReport {
  id: string
  tenantId: string
  scanDate: string
  scannedBy: string
  scubaVersion: string
  totalPasses: number
  totalFailures: number
  totalWarnings: number
  totalErrors: number
  totalManual: number
  totalControls: number
  complianceScore: number
  products: ScubaProductResult[]
  scanJobId?: string
}

export interface ScubaProductResult {
  productName: string
  productDisplayName: string
  passes: number
  failures: number
  warnings: number
  errors: number
  manual: number
  totalControls: number
  complianceScore: number
  controlGroups: ScubaControlGroup[]
}

export interface ScubaControlGroup {
  groupName: string
  groupNumber: string
  groupReferenceUrl?: string
  controls: ScubaControl[]
  passes: number
  failures: number
  warnings: number
  errors: number
  manual: number
}

export interface ScubaControl {
  controlId: string
  requirement: string
  result: ScubaControlResult
  criticality: string
  details: string
}

export type ScubaControlResult = 'Pass' | 'Fail' | 'Warning' | 'Error' | 'NotApplicable'

export interface ScubaSummary {
  tenantId: string
  hasReport: boolean
  lastScanDate?: string
  scannedBy?: string
  complianceScore: number
  totalPasses: number
  totalFailures: number
  totalWarnings: number
  totalErrors: number
  totalManual: number
  totalControls: number
  products: ScubaProductSummary[]
}

export interface ScubaProductSummary {
  productName: string
  productDisplayName: string
  complianceScore: number
  passes: number
  failures: number
  warnings: number
  totalControls: number
}

export interface ScubaSecurityRisk {
  productName: string
  productDisplayName: string
  groupName: string
  requirement: string
  details: string
  criticality: string
  result: ScubaControlResult
}

export interface ScubaScanResponse {
  jobId: string
  status: string
  message: string
}

// Product styling information for UI
export interface ScubaProductInfo {
  productName: string
  displayName: string
  iconName: string
  color: string
  bgColor: string
}

/** Neutral mark tone: SCuBA workloads are identified by name and icon, never by hue (DESIGN.md "The Category-Only Rule"). */
const NEUTRAL_MARK = 'var(--icon-secondary)'
const NEUTRAL_WELL = 'bg-layer-accent'

// Product styling map
export const SCUBA_PRODUCTS: Record<string, ScubaProductInfo> = {
  aad: {
    productName: 'aad',
    displayName: 'Entra ID',
    iconName: 'ShieldCheckIcon',
    color: NEUTRAL_MARK,
    bgColor: NEUTRAL_WELL
  },
  sharepoint: {
    productName: 'sharepoint',
    displayName: 'SharePoint',
    iconName: 'FolderIcon',
    color: NEUTRAL_MARK,
    bgColor: NEUTRAL_WELL
  },
  exo: {
    productName: 'exo',
    displayName: 'Exchange Online',
    iconName: 'EnvelopeIcon',
    color: NEUTRAL_MARK,
    bgColor: NEUTRAL_WELL
  },
  teams: {
    productName: 'teams',
    displayName: 'Teams',
    iconName: 'ChatBubbleLeftRightIcon',
    color: NEUTRAL_MARK,
    bgColor: NEUTRAL_WELL
  },
  defender: {
    productName: 'defender',
    displayName: 'Defender',
    iconName: 'ShieldExclamationIcon',
    color: NEUTRAL_MARK,
    bgColor: NEUTRAL_WELL
  }
}

// Helper function to get product info
export function getScubaProductInfo(productName: string): ScubaProductInfo {
  const normalizedName = productName.toLowerCase()
  return SCUBA_PRODUCTS[normalizedName] ?? {
    productName: normalizedName,
    displayName: productName,
    iconName: 'QuestionMarkCircleIcon',
    color: NEUTRAL_MARK,
    bgColor: NEUTRAL_WELL
  }
}

// Result badge classes (Carbon tags, DESIGN.md "Chips"). Neutral throughout:
// a failed or errored check takes the high-contrast tag (the Neutral Bad
// State Rule), the rest the default or outline tag, and the word says which.
export function getResultColor(result: ScubaControlResult): string {
  switch (result) {
    case 'Fail':
    case 'Error':
      return 'bg-inverse text-[color:var(--background)] font-semibold'
    case 'Pass':
    case 'Warning':
      return 'bg-layer-selected text-text-primary'
    case 'NotApplicable':
    default:
      return 'bg-transparent text-text-secondary ring-1 ring-inset ring-line-strong'
  }
}

// Result badge text, in sentence case (DESIGN.md "The No-Kicker Rule": nothing set in capitals)
export function getResultText(result: ScubaControlResult): string {
  switch (result) {
    case 'Pass':
      return 'Pass'
    case 'Fail':
      return 'Fail'
    case 'Warning':
      return 'Warning'
    case 'Error':
      return 'Error'
    case 'NotApplicable':
      return 'N/A'
    default:
      return result
  }
}
